<template>
  <MultiLevelNavigationModal
    v-model="localValue"
    :modal-title="$t('selectCatalogNodePath')"
    :fetch-branches="getCatalogNodes"
    :fetch-leaves="noLeaves"
    :selectable-type="['branch']"
    :base-path-title="$t('BaseCatalog')"
    @confirm="onConfirm"
    :selected-entry-id="selectedEntryId"
    :selected-path="selectedPath"
    @hide="emits('hide')"
    @cancel="emits('cancel')"
    @close="emits('close')" />
</template>

<script setup lang="ts">
import { useContentLanguages } from "@common/composable/useMetaDataStore";
import { BaseCatalog, CatalogNode } from "@medapro-types/common/common";
import { Branch, Leaf, MultiLevelNavigationConfirmModel } from "@medapro-types/common/components/MultiLevelNavigation";
import catalogsApi from "@services/catalogs.js";
import { computed, reactive } from "vue";
import MultiLevelNavigationModal from "./MultiLevelNavigationModal.vue";

//#region General
const emits = defineEmits(["confirm", "input", "cancel", "close", "hide"]);

const props = defineProps<{
  value: boolean;
  selectedEntryId: string | null | undefined;
  selectedPath?: Branch[] | null;
}>();
//#endregion

//#region Store
const { primaryLanguageCode } = useContentLanguages();
//#endregion

//#region Viewmodel
type ViewModel = { baseCatalog: BaseCatalog | null };
const vm = reactive<ViewModel>({ baseCatalog: null });
//#endregion

//#region EventHandler
const onConfirm = (event: MultiLevelNavigationConfirmModel) => {
  emits("confirm", event);
};
//#endregion

//#region localValue
const localValue = computed({
  get: () => props.value,
  set: (nv) => emits("input", nv)
});

//#endregion

//#region FetchMethods

const getCatalogNodes = async (id: string | null | undefined): Promise<Branch[]> => {
  if (!vm.baseCatalog) vm.baseCatalog = await catalogsApi.getBaseCatalog({ language: primaryLanguageCode.value });
  let result: Branch[] = [];
  let fetched: { catalogNodes: CatalogNode[]; foundCatalogNodeCount: number } = { catalogNodes: [], foundCatalogNodeCount: 0 };
  if (vm.baseCatalog) {
    if (id) {
      fetched = await catalogsApi.getNodes({
        catalogId: vm.baseCatalog.catalogId,
        language: primaryLanguageCode.value,
        catalogNodeId: id
      });
    } else {
      fetched = await catalogsApi.getRootNodes({
        catalogId: vm.baseCatalog.catalogId,
        language: primaryLanguageCode.value
      });
    }
    result = fetched.catalogNodes.map((x) => ({
      id: x.catalogNodeId,
      name: x.nodeName,
      parentId: x.rootCatalogNode.id,
      depth: 0,
      hasNoChildren: x.catalogNodeType != "CatalogGroup",
      icon: x.catalogNodeType == "CatalogGroup" ? "CatalogGroup" : "VariantGroups"
    }));
  }
  return result;
};

const noLeaves = async (): Promise<Leaf[]> => {
  return [];
};
//#endregion
</script>
